import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { useSession } from "@outschool/ui-auth";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
} from "react";

const LOCAL_STORAGE_KEY = "activeLearnerUid";

interface ActiveLearnerContextValue {
  activeLearner: ChildFragmentFragment | null;
  setActiveLearner: (learnerUid: string | null) => void;
}

const ActiveLearnerContext = createContext<ActiveLearnerContextValue>({
  activeLearner: null,
  setActiveLearner: () => {},
});

interface ActiveLearnerProviderProps {
  children: ReactNode;
}

export function ActiveLearnerProvider({
  children,
}: ActiveLearnerProviderProps) {
  const [activeLearnerUid, saveActiveLearnerUid] = useLocalStorageState<
    string | null
  >(LOCAL_STORAGE_KEY, null);
  const { currentUser } = useSession();

  const activeLearner =
    currentUser?.children.find(child => child?.uid === activeLearnerUid) ??
    null;

  const setActiveLearner = useCallback(
    (learnerUid: string | null) => {
      saveActiveLearnerUid(learnerUid);
    },
    [saveActiveLearnerUid],
  );

  return (
    <ActiveLearnerContext.Provider value={{ activeLearner, setActiveLearner }}>
      {children}
    </ActiveLearnerContext.Provider>
  );
}

interface UseActiveLearnerArgs {
  onActiveLearnerChanged?: (
    activeLearner: ChildFragmentFragment | null,
  ) => void;
}

export function useActiveLearner(opts: UseActiveLearnerArgs = {}) {
  const context = useContext(ActiveLearnerContext);
  const onActiveLearnerChangedRef = React.useRef(opts.onActiveLearnerChanged);

  if (context === undefined) {
    throw new Error(
      "useActiveLearner must be used within an ActiveLearnerProvider",
    );
  }

  // Keep ref up-to-date
  React.useEffect(() => {
    onActiveLearnerChangedRef.current = opts.onActiveLearnerChanged;
  }, [opts.onActiveLearnerChanged]);

  React.useEffect(() => {
    if (onActiveLearnerChangedRef.current) {
      onActiveLearnerChangedRef.current(context.activeLearner);
    }
  }, [context.activeLearner]);

  return context;
}
